/*
=========================================================
* FMP Soluções — Produtos / Licenças de Software
* Rota: /pages/produtos/licencas
=========================================================
*/

// @mui material components
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import { Link as RouterLink } from "react-router-dom";

// Material Kit 2 PRO React components
import MKBox from "components/MKBox";
import MKTypography from "components/MKTypography";
import MKButton from "components/MKButton";
import MKBadge from "components/MKBadge";

// Navbar / Footer
import DefaultNavbar from "examples/Navbars/DefaultNavbar";
import FMPFooter from "examples/Footers/FMPFooter";

// Routes
import routes from "routes";
import footerRoutes from "footer.routes";

// Image
import bgImage from "assets/images/produtos.jpg";

const products = [
  {
    name: "RevBits",
    tagline: "Plataforma unificada de cibersegurança",
    description:
      "Seis módulos integrados — do controle de acesso privilegiado à segurança de email — distribuídos com exclusividade pela FMP Soluções em toda a América do Sul.",
    icon: "security",
    status: "active",
    route: "/pages/produtos/licencas/RevBits",
    badge: "Disponível",
    badgeColor: "success",
  },
  {
    name: "5Eyes",
    tagline: "Inteligência e vigilância cibernética",
    description:
      "Plataforma de inteligência cibernética com capacidades avançadas de monitoramento, correlação e análise de ameaças em tempo real.",
    icon: "visibility",
    status: "soon",
    badge: "Em breve",
    badgeColor: "warning",
  },
  {
    name: "A.R.C.A.",
    tagline: "Acompanhamento e Rastreamento de Comportamentos em Ambientes Digitais",
    description:
      "Software proprietário da FMP Soluções para rastreamento comportamental em ambientes digitais — desenvolvido para logística, bancos, defesa e governo.",
    icon: "track_changes",
    status: "soon",
    badge: "Em breve",
    badgeColor: "warning",
  },
];

function ProductCard({ product }) {
  return (
    <MKBox
      borderRadius="lg"
      shadow="md"
      p={4}
      height="100%"
      display="flex"
      flexDirection="column"
      sx={{
        backgroundColor: ({ palette: { white } }) => white.main,
        border: ({ borders: { borderWidth, borderColor } }) =>
          `${borderWidth[1]} solid ${borderColor}`,
        opacity: product.status === "soon" ? 0.75 : 1,
        transition: "box-shadow 200ms ease",
        "&:hover": product.status === "active"
          ? { boxShadow: ({ boxShadows: { lg } }) => lg }
          : {},
      }}
    >
      {/* Ícone + Badge */}
      <MKBox display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
        <MKBox
          width="3.5rem"
          height="3.5rem"
          variant="gradient"
          bgColor={product.status === "active" ? "info" : "secondary"}
          color="white"
          coloredShadow={product.status === "active" ? "info" : "secondary"}
          display="flex"
          alignItems="center"
          justifyContent="center"
          borderRadius="xl"
        >
          <Icon fontSize="medium">{product.icon}</Icon>
        </MKBox>
        <MKBadge
          badgeContent={product.badge}
          color={product.badgeColor}
          variant="gradient"
          size="sm"
          container
        />
      </MKBox>

      {/* Texto */}
      <MKTypography variant="h4" mb={0.5}>
        {product.name}
      </MKTypography>
      <MKTypography variant="button" color="info" fontWeight="regular" mb={1} display="block">
        {product.tagline}
      </MKTypography>
      <MKTypography variant="body2" color="text" mb={3} sx={{ flexGrow: 1 }}>
        {product.description}
      </MKTypography>

      {/* CTA */}
      {product.status === "active" ? (
        <MKButton
          variant="gradient"
          color="info"
          component={RouterLink}
          to={product.route}
          fullWidth
        >
          Conhecer o produto
        </MKButton>
      ) : (
        <MKButton
          variant="outlined"
          color="secondary"
          component="a"
          href="/pages/ajuda/contate-nos"
          fullWidth
        >
          Receber novidades
        </MKButton>
      )}
    </MKBox>
  );
}

// PropTypes
import PropTypes from "prop-types";

ProductCard.propTypes = {
  product: PropTypes.shape({
    name: PropTypes.string.isRequired,
    tagline: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    icon: PropTypes.string.isRequired,
    status: PropTypes.oneOf(["active", "soon"]).isRequired,
    badge: PropTypes.string.isRequired,
    badgeColor: PropTypes.string.isRequired,
    route: PropTypes.string,
  }).isRequired,
};

function Licencas() {
  return (
    <>
      <DefaultNavbar
        routes={routes}
        action={{
          type: "internal",
          route: "/pages/ajuda/contate-nos",
          label: "Contate-nos",
          color: "info",
        }}
        sticky
      />

      {/* Hero */}
      <MKBox
        minHeight="45vh"
        width="100%"
        sx={{
          backgroundImage: ({ functions: { linearGradient, rgba }, palette: { gradients } }) =>
            `${linearGradient(
              rgba(gradients.dark.main, 0.6),
              rgba(gradients.dark.state, 0.6)
            )}, url(${bgImage})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          display: "grid",
          placeItems: "center",
        }}
      >
        <Container>
          <Grid container item xs={12} md={7} justifyContent={{ xs: "center", md: "start" }}>
            <MKTypography variant="h1" color="white" mb={1}>
              Licenças de Software
            </MKTypography>
            <MKTypography variant="h5" color="white" opacity={0.8}>
              Plataformas de cibersegurança distribuídas pela FMP Soluções com suporte local,
              integração especializada e conformidade com a legislação brasileira.
            </MKTypography>
          </Grid>
        </Container>
      </MKBox>

      {/* Cards */}
      <Card
        sx={{
          p: 2,
          mx: { xs: 2, lg: 3 },
          mt: -8,
          mb: 4,
          backgroundColor: ({ palette: { white }, functions: { rgba } }) => rgba(white.main, 0.8),
          backdropFilter: "saturate(200%) blur(30px)",
          boxShadow: ({ boxShadows: { xxl } }) => xxl,
        }}
      >
        <MKBox component="section" py={{ xs: 6, md: 10 }}>
          <Container>
            <Grid container justifyContent="center" mb={6}>
              <Grid item xs={12} md={8} textAlign="center">
                <MKTypography variant="h3" mb={1}>
                  Nossas plataformas de software
                </MKTypography>
                <MKTypography variant="body2" color="text">
                  A FMP Soluções atua como distribuidora exclusiva de soluções de software de
                  cibersegurança para a América do Sul, oferecendo licenciamento, implantação,
                  suporte nível 1 e 2, e integração com a sua infraestrutura existente.
                </MKTypography>
              </Grid>
            </Grid>

            <Grid container spacing={4}>
              {products.map((product, i) => (
                <Grid item xs={12} md={4} key={i}>
                  <ProductCard product={product} />
                </Grid>
              ))}
            </Grid>
          </Container>
        </MKBox>
      </Card>

      <MKBox pt={6} px={1} mt={6}>
        <FMPFooter content={footerRoutes} />
      </MKBox>
    </>
  );
}

export default Licencas;
